<script setup lang="ts">
import type { CircularMatchChatItem } from '~/models/ChatData';
import {use2DigitDateFormatter} from '~/composables/use-2-digit-date-formatter';

const { t, locale } = useI18n({
  useScope: 'local',
});

const props = defineProps<{
  tuid?: string;
}>();

const isDesktop = useIsDesktop();
const { chatAPI } = useBackendRequests();
const { formatDate } = use2DigitDateFormatter();
const { truncateText } = useTruncateText();
const shouldLoadMoreChatListItems = ref(true);
const allChatItems = ref<HTMLElement | null>(null);
const route = useRoute();
const targetUserId = (route.query.tuid as string) ?? props.tuid ?? '';

const targetUserGuid = ref('');
const targetUserDisplayName = ref('');
const targetUserImage = ref('');

const page = ref(1);
const perPage = ref(10);
const totalCount = ref(0);
const selectedFilter = ref('all');
const isLoading = ref(false);
const items = ref<CircularMatchChatItem[]>([]);
const scrollPosition = ref<number>();

const { data } = await useAsyncData('fetch-circular-matches', () => chatAPI.getCircularMatchChatListItems(targetUserId, selectedFilter.value, page.value, perPage.value));

if(data.value) {
  targetUserGuid.value = data.value.targetUser.guid;
  targetUserDisplayName.value = data.value.targetUser.displayName;
  targetUserImage.value = data.value.targetUser.image.urlPaths['50x50'];
  totalCount.value = data.value.totalCount;
  items.value = data.value.circularMatchChatListItems;
}

async function loadItems() {
  if (!shouldLoadMoreChatListItems.value || isLoading.value) {
    return;
  }
    isLoading.value = true;
  const response = await chatAPI.getCircularMatchChatListItems(targetUserId, selectedFilter.value, page.value, perPage.value);
    isLoading.value = false;
      items.value = [...items.value, ...response.circularMatchChatListItems];
      if (response.circularMatchChatListItems.length === 0) {
        shouldLoadMoreChatListItems.value = false;
      }

  nextTick(() => {
    if (allChatItems.value) {
      allChatItems.value.scrollTop = scrollPosition.value ?? 0;
    }
  });

}
const saveScrollPosition = (scrollTop: number) => {
  scrollPosition.value = scrollTop;
};


function updateFilter(filter: string) {
    items.value = [];
    shouldLoadMoreChatListItems.value = true;
    selectedFilter.value = filter;
    page.value = 1;
    loadItems();
}

function onScroll(event: Event): void {
  const element = event.target as HTMLElement;
  const offset = 20;
  saveScrollPosition(element.scrollTop);
  if (element.scrollTop + element.clientHeight + offset >= element.scrollHeight) {
    page.value += 1;
    loadItems();
  }
}

onMounted(() => {
  const chatListElement = allChatItems.value;
  if (chatListElement) {
    chatListElement.addEventListener('scroll', onScroll);
  }
});

onBeforeUnmount(() => {
  const chatListElement = allChatItems.value;
  if (chatListElement) {
    chatListElement.removeEventListener('scroll', onScroll);
  }
});
</script>

<template>
  <section class="tw-all-chats">
    <tw-back-view>
      <template #header>
        <div class="tw-all-chats-header">
          <tw-rt-all-chats-header
            :possibilities="totalCount"
            @update:filter="updateFilter($event)"
          />
        </div>
      </template>
      <div class="tw-all-chats-main" ref="allChatItems">
        <p v-if="items.length === 0 && !isLoading" class="tw-info">{{ t('no-items') }}</p>
        <div v-if="isLoading" class="tw-info">
          <Icon name="line-md:loading-twotone-loop"  class="tw-loading-indicator" />
        </div>
        <ul v-if="items.length > 0">
          <li
            v-for="item in items"
            :key="item.intermediateUser.guid"
            class="item"
          >
            <tw-rt-all-chats-item>
              <template #header>
                <tw-chat-room-avatar-names-display
                  :target-user-name="targetUserDisplayName"
                  :target-user-image="targetUserImage"
                  :intermediate-user-name="item.intermediateUser.displayName"
                  :intermediate-user-image="item.intermediateUser.image.urlPaths['50x50']"
                >
                  <template v-if="!item.lastMessage">
                    <p class="item-hint">{{ t('no-messages') }}</p>
                  </template>
                  <template v-else>
                    <p class="item-hint">
                      {{ formatDate(item.lastMessage.timestamp.date, locale) }} :
                    </p>
                    <p class="item-lastmessage">
                      {{ truncateText(item.lastMessage.message, isDesktop ? 180 : 80, true) }}
                    </p>
                  </template>
                </tw-chat-room-avatar-names-display>
              </template>
              <template #footer>
                <div class="item-footer">
                  <p>{{ t('interest-count', [item.interestCount]) }}</p>
                  <tw-ui-action
                    variant="text-primary"
                    is="link"
                    :to="`/messaging/chat/${targetUserGuid}/${item.intermediateUser.guid}`"
                  >
                    {{ item.hasStarted ? t('chat-link', 0) : t('chat-link', 1) }}
                  </tw-ui-action>
                </div>
              </template>
            </tw-rt-all-chats-item>
          </li>
        </ul>
      </div>
    </tw-back-view>
  </section>
</template>


<style scoped lang="scss">
@import '@/assets/styles/tw-variables.scss';
@import '@/assets/styles/tw-mixins.scss';

.tw-all-chats {
  background-color: $neutral-5;
  height: 100%;
  padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  .tw-all-chats-header {
    background-color: $white;
    padding: 0 1rem 1rem;
    @include tw-is-desktop {
      padding: 1rem;
    }
  }
  .tw-all-chats-main {
    padding: 1rem;
    max-height: 70vh;
    overflow-y: auto;
    @include tw-is-desktop {
      min-height: 400px;
      padding-bottom: 1rem;
    }
    p {
      margin: 0;
    }
    .item {
      margin: 1rem 0;
      &-hint {
        font-size: 0.75rem;
        color: $neutral-variant-100;
        margin-top: 0.5rem;
      }
        &-lastmessage {
          word-break: break-word;
          overflow-wrap: break-word;
          font-size: 0.875rem;
        }
    }
    .item-footer {
      display: flex;
      justify-content: space-between;
    }
  }
  .tw-all-chat-items {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  :deep(.mobile-header) {
    background-color: $white;
    padding: 1rem;
  }
}
.tw-back-wrapper {
  display: flex;
  flex-direction: column;
}

.tw-info {
  text-align: center;
}

.tw-loading-indicator {
  font-size: 2.5rem;
}
</style>

<i18n lang="json">
{
  "de": {
   "chat-link": "Chat starten | Zum Chat",
    "no-messages": "Noch keine Nachrichten",
    "interest-count": "{0}/3 haben Interesse",
    "no-items": "Keine Einträge gefunden"
  },
  "en": {
    "chat-link": "Start chat | Go to chat",
    "no-messages": "No messages yet",
    "interest-count": "{0}/3 are interested",
    "no-items": "No entries found"
  }
}
</i18n>
